import { Link } from "wouter";
import { ReactNode, useState } from "react";
import { BarChart3, Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useT } from "@/lib/i18n";
import { useLocale } from "@/lib/locale-context";
import { LanguageSwitcher } from "@/components/language-switcher";
import { YourHackBadge } from "@/components/yourhack-badge";
import { MARKET_POSITIONING_LINE } from "@/lib/authoritative-messaging";

const INTELLIGENCE_ITEMS = [
  { key: "intelligence_library", path: "/r" },
  { key: "market_districts",     path: "/market-districts" },
  { key: "market_history",       path: "/market-history" },
  { key: "scarcity_alpha",       path: "/scarcity-alpha" },
  { key: "market_catalyst",      path: "/market-catalyst" },
  { key: "methodology",          path: "/methodology" },
] as const;

export function Layout({ children }: { children: ReactNode }) {
  const t = useT();
  const { localizePath } = useLocale();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [intelligenceOpen, setIntelligenceOpen] = useState(false);
  const [mobileIntelOpen, setMobileIntelOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col font-sans">
      <header className="border-b border-border bg-background sticky top-0 z-[60]">
        <div className="container max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link href={localizePath("/")} className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="bg-primary text-primary-foreground p-1.5 flex items-center justify-center transition-transform group-hover:scale-105">
              <BarChart3 className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-xl tracking-tight uppercase">{t("brand.name")}</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
            <Link
              href={localizePath("/properties")}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-properties"
            >
              {t("nav.properties")}
            </Link>

            {/* Intelligence dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIntelligenceOpen(true)}
              onMouseLeave={() => setIntelligenceOpen(false)}
            >
              <button
                className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                data-testid="nav-intelligence"
                aria-expanded={intelligenceOpen}
                aria-haspopup="true"
              >
                {t("nav.intelligence")}
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-150 ${intelligenceOpen ? "rotate-180" : ""}`}
                />
              </button>

              {intelligenceOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50 w-52">
                  <div className="bg-background border border-border shadow-lg">
                    {INTELLIGENCE_ITEMS.map((item) => (
                      <Link
                        key={item.key}
                        href={localizePath(item.path)}
                        className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors border-b border-border last:border-b-0"
                        data-testid={`nav-dropdown-${item.key}`}
                      >
                        {t(`nav.${item.key}`)}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <LanguageSwitcher compact />
          </nav>

          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher compact />
            <Button
              variant="ghost"
              size="icon"
              data-testid="nav-menu"
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen((o) => !o)}
            >
              <Menu className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-72 flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 py-5 border-b border-border">
            <SheetTitle className="text-left text-sm uppercase tracking-wider font-bold">
              {t("brand.name")}
            </SheetTitle>
          </SheetHeader>
          <nav className="flex flex-col py-4">
            <Link
              href={localizePath("/properties")}
              data-testid="mobile-nav-properties"
              className="px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("nav.properties")}
            </Link>

            {/* Intelligence accordion */}
            <button
              className="px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center justify-between w-full text-left"
              onClick={() => setMobileIntelOpen((o) => !o)}
              data-testid="mobile-nav-intelligence"
              aria-expanded={mobileIntelOpen}
            >
              {t("nav.intelligence")}
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-150 ${mobileIntelOpen ? "rotate-180" : ""}`}
              />
            </button>

            {mobileIntelOpen && (
              <div className="border-y border-border bg-muted/30">
                {INTELLIGENCE_ITEMS.map((item) => (
                  <Link
                    key={item.key}
                    href={localizePath(item.path)}
                    data-testid={`mobile-nav-${item.key}`}
                    className="block pl-10 pr-6 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                ))}
              </div>
            )}

            <div className="px-6 py-3 border-t border-border mt-2">
              <LanguageSwitcher compact />
            </div>
          </nav>
        </SheetContent>
      </Sheet>

      <main className="flex-1 flex flex-col">{children}</main>

      <footer className="border-t border-border bg-card py-12 mt-auto">
        <div className="container max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-primary text-primary-foreground p-1 flex items-center justify-center">
                <BarChart3 className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <span className="font-bold text-lg tracking-tight uppercase">{t("brand.name")}</span>
            </div>
            <p className="text-sm text-muted-foreground max-w-xs">{MARKET_POSITIONING_LINE}</p>
          </div>
          <div>
            <h4 className="font-bold mb-4 uppercase text-xs tracking-wider text-muted-foreground">
              {t("footer.platform_title")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li><Link href={localizePath("/properties")} className="hover:text-primary transition-colors">{t("footer.property_search")}</Link></li>
              <li><Link href={localizePath("/r")} className="hover:text-primary transition-colors">{t("footer.intelligence_library")}</Link></li>
              <li><Link href={localizePath("/market-benchmarks")} className="hover:text-primary transition-colors">{t("footer.market_benchmarks")}</Link></li>
              <li><Link href={localizePath("/market-history")} className="hover:text-primary transition-colors">{t("nav.market_history")}</Link></li>
              <li><Link href={localizePath("/market-catalyst")} className="hover:text-primary transition-colors">{t("nav.market_catalyst")}</Link></li>
              <li><Link href={localizePath("/market-districts")} className="hover:text-primary transition-colors">{t("nav.market_districts")}</Link></li>
              <li><Link href={localizePath("/scarcity-alpha")} className="hover:text-primary transition-colors">{t("nav.scarcity_alpha")}</Link></li>
              <li><Link href={localizePath("/methodology")} className="hover:text-primary transition-colors">{t("footer.methodology")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-4 uppercase text-xs tracking-wider text-muted-foreground">
              {t("footer.legal_title")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li><Link href={localizePath("/terms")} className="hover:text-primary transition-colors">{t("footer.terms_of_service")}</Link></li>
              <li><Link href={localizePath("/privacy")} className="hover:text-primary transition-colors">{t("footer.privacy_policy")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="container max-w-6xl mx-auto px-4 mt-8 pt-6 border-t border-border/60">
          <YourHackBadge />
        </div>
      </footer>
    </div>
  );
}
